import { InfoBox, Modal, tokens, ValXmlPlaceholderImage } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Box, Stack } from '@mui/material'
import { LightbulbFilamentIcon, NotEqualsIcon, WarningIcon } from '@phosphor-icons/react'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useTranslation } from 'react-i18next'

export type ValXmlDownloadModalProps = {
    isOpen: boolean
    onClose: () => void
    onDownload: () => void
    isDownloading?: boolean
    mode?: 'single' | 'multiple'
    hasValuationsWhichCannotBeExportedToValXML?: boolean
    isDraft?: boolean
}

export const ValXmlDownloadModal = ({
    isOpen,
    onClose,
    onDownload,
    hasValuationsWhichCannotBeExportedToValXML = false,
    isDownloading = false,
    mode = 'single',
    isDraft = false,
}: ValXmlDownloadModalProps) => {
    const { t } = useTranslation('terminology', { keyPrefix: 'common.valXmlDownloadModal' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t(`${mode}.title`)}
            showCloseIcon
            closeIconTooltip={tCommonActions('close')}
            showLoading={isDownloading}
            footerProps={{
                primaryButton: {
                    text: t('download'),
                    onClick: onDownload,
                    disabled: isDownloading,
                },
                secondaryButton: {
                    text: t('cancel'),
                    onClick: onClose,
                    disabled: isDownloading,
                },
            }}
        >
            <Stack direction="column">
                <Stack alignItems="stretch" marginBottom={tokens.SizingSpacing300}>
                    <ValXmlPlaceholderImage width="100%" height="auto" />
                </Stack>
                <Headline variant="headline3" title={t(`${mode}.headline`)} withSpacing />
                <Stack direction="column" gap={tokens.SizingSpacing150}>
                    <Stack direction="row" alignItems="center">
                        <Box width={tokens.SizingIconM} display="flex" paddingRight={tokens.SizingSpacing100}>
                            <LightbulbFilamentIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                        </Box>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {t(`${mode}.paragraphOne`)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="flex-start">
                        <Box width={tokens.SizingIconM} display="flex" paddingRight={tokens.SizingSpacing100}>
                            <NotEqualsIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                        </Box>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {t(`${mode}.notEqual`)}
                        </Typography>
                    </Stack>
                    {hasValuationsWhichCannotBeExportedToValXML && (
                        <InfoBox variant="warning" icon={<WarningIcon />}>
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {t('notAllConvertible')}
                            </Typography>
                        </InfoBox>
                    )}
                    {isDraft && (
                        <Stack direction="row" alignItems="flex-start">
                            <Box width={tokens.SizingIconM} display="flex" paddingRight={tokens.SizingSpacing100}>
                                <WarningIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                            </Box>
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {t('draftWarning')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Modal>
    )
}
